/**
 * ShareMusicPanel Component
 * 
 * Purpose: Overlay panel that slides down from the dashboard header to enable music sharing.
 * Allows users to paste streaming platform URLs (Spotify, Bandcamp, SoundCloud), resolve them 
 * to music entities, and create contextual content items that appear in the commons feed.
 * 
 * Features:
 * - URL input and validation for multiple music platforms
 * - Music resolution via backend API
 * - Progressive disclosure (URL → preview → comment/review)
 * - Different flows for tracks vs albums (word count requirements)
 * - Error handling and user feedback
 * - Mobile responsive design with accessibility support
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Music, Loader2 } from 'lucide-react';
import { resolveMusicLink, createContentItem, getEntityContent, createComment, submitPendingMusicContent } from '../../api/api';
import { MusicLinkResolveRequest, MusicLinkResolveResponse, EntityType, MusicTrackDisplay, MusicReleaseDisplay, YouTubeVideoDisplay, ContentType, OriginLocation } from 'shared/types/contentTypes';
import { useAuthContext } from '../Auth/AuthContext/AuthContext';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { validateMusicUrl, detectPlatform } from '../../utils/musicUrl';

interface ShareMusicPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onContentCreated?: () => void;
  // Configuration props for different contexts
  headerText?: string;
  explainerText?: string;
  urlPlaceholder?: string;
  defaultContentType?: 'discussion' | 'review' | 'listeningRoom';
  // Props for sharing TO a user's dancefloor
  targetUser?: { userId: string; displayName: string };
  originLocation?: OriginLocation;
}

type PanelState = 'url-input' | 'loading' | 'music-resolved' | 'confirm-artist' | 'error' | 'pending-success';

type ShareMusicError =
  | 'invalid-url'
  | 'unsupported-platform'
  | 'network-error'
  | 'music-not-found'
  | 'artist-pending'
  | 'artist-unidentified'
  | 'artist-ambiguous'
  | 'catalog-pending'
  | 'artist-not-found'
  | 'already-shared'
  | 'unknown';

interface ResolvedMusicData {
  response: MusicLinkResolveResponse;
  originalUrl: string;
  isPending?: boolean;
}

const ShareMusicPanel: React.FC<ShareMusicPanelProps> = (props) => {
  const {
    isOpen,
    onClose,
    onContentCreated,
    headerText = "Share what's moving you",
    explainerText = "Comment on a track • Write a review • Add to Listening Room",
    urlPlaceholder = "Paste album or track URL...",
    defaultContentType = 'discussion',
    targetUser,
    originLocation
  } = props;
  const [panelState, setPanelState] = useState<PanelState>('url-input');
  const [musicUrl, setMusicUrl] = useState('');
  const [resolvedMusic, setResolvedMusic] = useState<ResolvedMusicData | null>(null);
  const [commentText, setCommentText] = useState('');
  const [error, setError] = useState<ShareMusicError | null>(null);
  const [isCreatingContent, setIsCreatingContent] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState<'discussion' | 'review' | 'listeningRoom'>(props.defaultContentType || 'discussion');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  
  const urlInputRef = useRef<HTMLInputElement>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { userProfile } = useAuthContext();
  
  const loadingMessages = [
    { action: "Retrieving URL", thought: "The word 'contribute' comes from 'con' (together) and 'tribute' (gift)" },
    { action: "Searching discography", thought: "—which itself shares roots with 'tribe.'" },
    { action: "Retrieving artist", thought: "To contribute is to give together as a tribe." },
    { action: "Exploring album art", thought: "It teaches us how we create together." },
    { action: "Finalizing details", thought: "What can we make together?" }
  ];

  // Auto-focus URL input when panel opens
  useEffect(() => {
    if (isOpen && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isOpen]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = commentTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(80, textarea.scrollHeight)}px`;
    }
  }, [commentText]);
  
  // Cycle through loading messages
  useEffect(() => {
    if (panelState === 'loading') {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 4500);
      
      return () => clearInterval(interval);
    }
  }, [panelState, loadingMessages.length]);

  // Reset state when panel closes or defaultContentType changes
  useEffect(() => {
    if (!isOpen) {
      setPanelState('url-input');
      setMusicUrl('');
      setResolvedMusic(null);
      setCommentText('');
      setError(null);
      setIsCreatingContent(false);
      setSelectedContentType(defaultContentType);
      setLoadingMessageIndex(0);
    }
  }, [isOpen, defaultContentType]);

  // Handle ESC key to close panel
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleUrlSubmit = useCallback(async () => {
    if (!musicUrl.trim()) return;

    // Validate URL format
    if (!validateMusicUrl(musicUrl)) {
      setError('invalid-url');
      setPanelState('error');
      return;
    }

    setPanelState('loading');
    setError(null);

    const request: MusicLinkResolveRequest = { musicUrl: musicUrl.trim() };

    try {
      const response = await resolveMusicLink(request);

      // Pending covers three different outcomes, so the reason decides what we tell the person.
      // Without it we promised "being added, try again" even when nothing was in flight (a dead
      // end) and even when the artist was already on Coral (a success reported as a failure).
      if (response.pendingArtist) {
        setError(
          response.pendingReason === 'artist-unidentified' ? 'artist-unidentified'
          : response.pendingReason === 'artist-ambiguous' ? 'artist-ambiguous'
          : response.pendingReason === 'catalog-pending' ? 'catalog-pending'
          : 'artist-pending'
        );
        setPanelState('error');
        return;
      }

      // Handle YouTube-specific responses
      if (response.type === 'youtubeVideo') {
        // Check for low confidence - show confirmation
        if (response.requiresConfirmation || response.confidence === 'low') {
          setResolvedMusic({ response, originalUrl: musicUrl });
          setPanelState('confirm-artist');
          return;
        }
      }

      // Check if the response contains pending entities (Spotify flow)
      const isPendingEntity = response.type === 'track'
        ? (response.data as MusicTrackDisplay).isPending
        : response.type === 'release'
          ? (response.data as MusicReleaseDisplay).isPending
          : false;

      setResolvedMusic({ response, originalUrl: musicUrl, isPending: isPendingEntity });
      setPanelState('music-resolved');

      // Track successful resolution
      trackEvent(EventName.MUSIC_URL_RESOLVED, {
        userId: userProfile?.userId,
        platform: detectPlatform(musicUrl),
        entityType: response.type
      });

    } catch (err) {
      console.error('Music resolution failed:', err);
      
      // Check if it's a 404 error by examining the error message or making a direct fetch
      // Since resolveMusicLink uses the generic fetchFromAPI, we need to detect 404 differently
      try {
        const tokens = await import('../../utils/authUtils').then(m => m.getCognitoTokens());
        if (tokens) {
          const apiEndpoint = process.env.REACT_APP_API_GATEWAY_ENDPOINT;
          const testResponse = await fetch(`${apiEndpoint}/music/resolve-link`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${tokens.idToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          });
          
          if (testResponse.status === 404) {
            setError('music-not-found');
          } else {
            setError('network-error');
          }
        } else {
          setError('network-error');
        }
      } catch {
        setError('network-error');
      }
      
      setPanelState('error');
    }
  }, [musicUrl, userProfile?.userId]);

  // Auto-resolve music URL when valid URL is detected (with debouncing)
  useEffect(() => {
    if (!musicUrl.trim() || panelState === 'loading' || panelState === 'music-resolved' || panelState === 'confirm-artist' || panelState === 'error' || panelState === 'pending-success') {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (validateMusicUrl(musicUrl)) {
        handleUrlSubmit();
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
  }, [musicUrl, panelState, handleUrlSubmit]);

  // Handle textarea key events for Enter-to-submit
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (commentText.trim() && isContentValid()) {
        handleCreateContent();
      }
    }
  };

  const handleCreateContent = async () => {
    if (!resolvedMusic || !commentText.trim()) return;

    // Validate content based on selected type requirements
    if (!isContentValid()) {
      return; // Button should be disabled, but just in case
    }

    // Handler-level guard to prevent double-submission from rapid taps/clicks
    if (isCreatingContent) return;

    setIsCreatingContent(true);

    try {
      const { response } = resolvedMusic;
      
      // Check if this is a pending entity
      if (resolvedMusic?.isPending) {
        // Handle pending entity submission
        const isTrack = response.type === 'track';
        const contentType: ContentType = isTrack ? 'discussion' : selectedContentType;
        
        // Submit pending content with simplified data
        await submitPendingMusicContent(
          resolvedMusic.originalUrl,
          contentType,
          commentText
        );
        
        // Track event
        trackEvent(EventName.MUSIC_CONTENT_SHARED, { 
          userId: userProfile?.userId,
          entityType: response.type,
          platform: detectPlatform(resolvedMusic.originalUrl),
          isPending: true
        });
        
        // Show success message
        setPanelState('pending-success');
        return;
      }
      
      // Handle normal entity submission (existing flow)
      // Determine entity type and ID based on response type
      let entityType: EntityType;
      let entityId: string;

      if (response.type === 'track') {
        entityType = 'musicTrack';
        entityId = (response.data as MusicTrackDisplay).trackId;
      } else if (response.type === 'youtubeVideo') {
        entityType = 'youtubeVideo';
        entityId = (response.data as YouTubeVideoDisplay).videoId;
      } else {
        entityType = 'musicRelease';
        entityId = (response.data as MusicReleaseDisplay).releaseId;
      }

      // Get existing content items for the entity (same as useEntityComments)
      const entityContentResponse = await getEntityContent(entityType, entityId);
      const existingContentItems = entityContentResponse?.contentFeed?.items || [];

      // Determine content type first
      // Tracks and YouTube videos only support discussion
      const isTrack = response.type === 'track';
      const isYouTubeVideo = response.type === 'youtubeVideo';
      const contentType: ContentType = (isTrack || isYouTubeVideo) ? 'discussion' : selectedContentType;

      // When sharing TO a dancefloor, always create new content (don't reuse existing discussions)
      const isDancefloorShare = !!targetUser;

      // Only reuse existing content items for discussions (not dancefloor shares)
      if (contentType === 'discussion' && existingContentItems.length > 0 && !isDancefloorShare) {
        // Use existing discussion and create a comment
        await createComment({
          contentId: existingContentItems[0].contentId,
          text: commentText,
          entityType,
          entityId
        });
      } else {
        // Create new content item for reviews, listening room, new discussions, or dancefloor shares
        // Build relatedData with targetUser if sharing to dancefloor
        const relatedData: Record<string, unknown> = {};
        if (targetUser) {
          relatedData.targetUser = targetUser;
        }

        // Determine content item text based on entity type
        const entityLabel = isYouTubeVideo ? 'Video' : isTrack ? 'Track' : 'Album';
        const contentItemText = `${entityLabel} shared: ${response.data.title}`;

        const newContentItem = await createContentItem({
          contentType,
          text: contentItemText,
          entityType,
          entityId,
          ...(Object.keys(relatedData).length > 0 && { relatedData }),
          ...(originLocation && { originLocation })
        });

        // ALWAYS create a comment with the user's actual contribution
        await createComment({
          contentId: newContentItem.contentId,
          text: commentText,
          entityType,
          entityId
        });
      }

      // Track successful content creation
      trackEvent(EventName.MUSIC_CONTENT_SHARED, { 
        userId: userProfile?.userId,
        entityType: response.type,
        platform: detectPlatform(resolvedMusic.originalUrl)
      });

      // Refresh the content feed to show the new content at the top
      if (onContentCreated) {
        onContentCreated();
      }

      // Close panel on success for real entities
      onClose();

    } catch (err) {
      console.error('Content creation failed:', err);
      setError('network-error');
      setPanelState('error');
    } finally {
      setIsCreatingContent(false);
    }
  };

  const getWordCount = (): number => {
    return commentText.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const getContentTypeRequirements = () => {
    const isTrack = resolvedMusic?.response.type === 'track';
    const isYouTubeVideo = resolvedMusic?.response.type === 'youtubeVideo';

    // For dancefloor shares, use personalized placeholder
    if (targetUser) {
      return {
        minWords: 0,
        placeholder: `Add a message for ${targetUser.displayName}...`
      };
    }

    if (isTrack) {
      // Tracks only support discussion
      return {
        minWords: 0,
        placeholder: "What do you love about this track?"
      };
    }

    if (isYouTubeVideo) {
      // YouTube videos only support discussion
      const videoData = resolvedMusic?.response.data as YouTubeVideoDisplay;
      const isLive = videoData?.videoType === 'livePerformance';
      return {
        minWords: 0,
        placeholder: isLive
          ? "What stands out about this performance?"
          : "Share your thoughts about this video..."
      };
    }

    // Albums support all three types
    switch (selectedContentType) {
      case 'discussion':
        return {
          minWords: 0,
          placeholder: "Start a conversation about this album..."
        };
      case 'review':
        return {
          minWords: 50,
          placeholder: "Share your detailed thoughts on this album..."
        };
      case 'listeningRoom':
        return {
          minWords: 25,
          placeholder: "Why is this album worth discovering?"
        };
      default:
        return {
          minWords: 0,
          placeholder: "Share your thoughts..."
        };
    }
  };

  const isContentValid = (): boolean => {
    const requirements = getContentTypeRequirements();
    const wordCount = getWordCount();
    return wordCount >= requirements.minWords;
  };

  const getErrorMessage = (): string => {
    switch (error) {
      case 'invalid-url':
        return "Hmm, we couldn't recognize that link. Try Spotify or YouTube.";
      case 'unsupported-platform':
        return "This platform isn't supported yet. Try Spotify or YouTube.";
      case 'network-error':
        return "Something went wrong. Please check your connection and try again.";
      case 'music-not-found':
        return "We've been unable to match that artist on third-party sources yet.";
      case 'artist-pending':
        return "This artist is being added to Coral. Try again in a few minutes.";
      case 'artist-ambiguous':
        return "More than one artist goes by this name, and we couldn't tell which one this release belongs to.";
      case 'artist-unidentified':
        return "We've been unable to match this artist on third-party sources, so we haven't added them yet.";
      case 'catalog-pending':
        return "This artist is on Coral. We're still matching this particular release, so try again shortly \u2014 or share another one of theirs.";
      case 'artist-not-found':
        return "We couldn't identify the artist in this video, or we haven't been able to match them on third-party sources yet.";
      case 'already-shared':
        return "You've already shared this. Add to the conversation instead?";
      default:
        return "An unexpected error occurred. Please try again.";
    }
  };

  const renderUrlInput = () => {
    const isValidUrl = musicUrl.trim() && validateMusicUrl(musicUrl);
    const platform = isValidUrl ? detectPlatform(musicUrl) : '';
    
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-nunito font-semibold text-gray-900 mb-2">{headerText}</h2>
          <p className="text-sm font-light text-gray-600 leading-relaxed">
            {explainerText}
          </p>
        </div>
        
        <div className="mb-4">
          <div className="relative">
            <input
              ref={urlInputRef}
              type="text"
              value={musicUrl}
              onChange={(e) => setMusicUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
              placeholder={urlPlaceholder}
              className={`w-full font-light px-4 py-3 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 placeholder-gray-400 transition-all duration-200 ${
                isValidUrl 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-200/50 bg-white'
              }`}
            />
            {isValidUrl && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  {platform} ✓
                </span>
              </div>
            )}
          </div>
          {isValidUrl && (
            <p className="text-xs text-green-600 mt-2 flex items-center">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Auto-fetching music info...
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    const currentMessage = loadingMessages[loadingMessageIndex] || loadingMessages[0];
    
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-6 mb-6">
          <div className="flex items-center mb-4">
            <Loader2 className="w-5 h-5 text-coral-pink animate-spin mr-3" />
            <span className="text-gray-700 text-sm">{currentMessage.action}...</span>
          </div>
          <p className="text-sm text-gray-700 font-medium text-center max-w-sm animate-fadeIn leading-relaxed">
            &ldquo;{currentMessage.thought}&rdquo;
          </p>
        </div>
      
      <div className="mb-4">
        <div className="relative">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            onFocus={() => setIsCommentFocused(true)}
            onBlur={() => setIsCommentFocused(commentText.trim().length > 0)}
            placeholder={targetUser ? `Add a message for ${targetUser.displayName}...` : "Add your thoughts about this track..."}
            className="w-full font-light px-4 py-3 border border-gray-200/50 rounded-lg overflow-hidden min-h-[80px] text-base focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 bg-white placeholder-gray-400 transition-all duration-200 resize-none"
            style={{ minHeight: '80px' }}
          />
        </div>
        
        {(isCommentFocused || commentText.trim()) && (
          <div className="mt-2 animate-fadeIn">
            <p className="text-xs font-light text-gray-500">
              Press Enter to submit once track loads, Shift+Enter for new line
            </p>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderMusicResolved = () => {
    if (!resolvedMusic) return null;

    const { response } = resolvedMusic;
    const data = response.data;
    const isTrack = response.type === 'track';
    const isYouTubeVideo = response.type === 'youtubeVideo';
    const wordCount = getWordCount();
    const isValid = isContentValid();
    const requirements = getContentTypeRequirements();

    // Get cover art / thumbnail based on content type
    const coverArtUrl = isYouTubeVideo
      ? (data as YouTubeVideoDisplay).thumbnailUrl
      : isTrack
        ? (data as MusicTrackDisplay).coverArtUrl
        : (data as MusicReleaseDisplay).coverArtUrl;

    // Get artist name based on content type
    const artistName = isYouTubeVideo
      ? (data as YouTubeVideoDisplay).artistName
      : isTrack
        ? (data as MusicTrackDisplay).artistName
        : (data as MusicReleaseDisplay).artistName;

    // YouTube video subtitle info
    const youtubeSubtitle = isYouTubeVideo
      ? (() => {
          const videoData = data as YouTubeVideoDisplay;
          if (videoData.videoType === 'livePerformance' && videoData.eventName) {
            return videoData.eventName;
          }
          if (videoData.videoType === 'musicVideo' && videoData.trackTitle) {
            return `Music Video • ${videoData.trackTitle}`;
          }
          return videoData.channelName;
        })()
      : null;

    return (
      <div className="p-6">
        <div className="flex items-start mb-6">
          {coverArtUrl && (
            <div className="relative mr-4 flex-shrink-0">
              <img
                src={coverArtUrl}
                alt={`${data.title} cover`}
                className={isYouTubeVideo
                  ? "w-32 h-20 rounded-lg object-cover shadow-lg"
                  : "w-20 h-20 rounded-2xl object-cover shadow-lg"
                }
              />
              {/* Overlay with play/music icon */}
              <div className={`absolute inset-0 bg-black/20 ${isYouTubeVideo ? 'rounded-lg' : 'rounded-2xl'} flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200`}>
                {isYouTubeVideo ? (
                  <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                    <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-coral-pink border-b-[6px] border-b-transparent ml-1" />
                  </div>
                ) : (
                  <Music className="w-6 h-6 text-white" />
                )}
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold text-gray-900 leading-tight mb-1 ${isYouTubeVideo ? 'text-base line-clamp-2' : 'text-xl'}`}>
              {data.title}
            </h3>
            <p className="text-coral-orange font-medium">
              {artistName}
            </p>
            {youtubeSubtitle && (
              <p className="text-sm text-gray-500 mt-0.5">{youtubeSubtitle}</p>
            )}
          </div>
        </div>

        {/* Tab Switcher for Albums (not shown for tracks or YouTube videos) */}
        {!isTrack && !isYouTubeVideo && (
          <div className="flex justify-center mb-4">
            <nav className="inline-flex bg-gray-100/50 backdrop-blur-sm p-1 rounded-full shadow-sm">
              <button
                onClick={() => setSelectedContentType('discussion')}
                className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  selectedContentType === 'discussion'
                    ? 'bg-white text-coral-deep-blue shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Discuss
              </button>
              <button
                onClick={() => setSelectedContentType('review')}
                className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  selectedContentType === 'review'
                    ? 'bg-white text-coral-deep-blue shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Review
              </button>
              <button
                onClick={() => setSelectedContentType('listeningRoom')}
                className={`relative px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  selectedContentType === 'listeningRoom'
                    ? 'bg-white text-coral-deep-blue shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center">
                  Recommend
                  <span className="ml-1 text-xs text-coral-orange">★</span>
                </span>
              </button>
            </nav>
          </div>
        )}
        
        {/* Explainer text */}
        {!isTrack && !isYouTubeVideo && (
          <div className="mb-5">
            <p className="text-sm font-light text-gray-600 leading-relaxed text-center">
              {selectedContentType === 'discussion' && 'Start a conversation or share quick thoughts'}
              {selectedContentType === 'review' && 'Deep dive with your detailed perspective'}
              {selectedContentType === 'listeningRoom' && (
                explainerText.includes('complete albums')
                  ? 'Curate complete albums for others to discover. The Listening Room is for albums that reward intentional listening'
                  : 'The Listening Room is for complete albums that reward intentional listening'
              )}
            </p>
          </div>
        )}

        <div className="mb-4">
          <div className="relative">
            <textarea
              ref={commentTextareaRef}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              onFocus={() => setIsCommentFocused(true)}
              onBlur={() => setIsCommentFocused(commentText.trim().length > 0)}
              placeholder=" "
              className="w-full font-light px-4 pt-6 pb-3 border border-gray-200/50 rounded-lg overflow-hidden min-h-[80px] text-base focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 bg-white placeholder-gray-400 transition-all duration-200 resize-none peer"
              style={{ minHeight: '80px' }}
            />
            <label className={`absolute left-4 text-gray-500 transition-all duration-200 pointer-events-none ${
              commentText || isCommentFocused 
                ? 'top-2 text-xs' 
                : 'top-4 text-base'
            }`}>
              {requirements.placeholder}
            </label>
          </div>
          
          {(isCommentFocused || commentText.trim()) && (
            <div className="mt-2 space-y-2 animate-fadeIn">
              {(isCommentFocused || commentText.trim()) && (
                <p className="text-xs font-light text-gray-500">
                  Press Enter to submit, Shift+Enter for new line
                </p>
              )}
              
              {requirements.minWords > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Min {requirements.minWords} words
                    {(requirements.minWords === 25 || requirements.minWords === 50) && ' • Give someone a reason to dedicate time to this album'}
                  </span>
                  {wordCount >= requirements.minWords && (
                    <div className="flex items-center text-green-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200/50 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateContent}
            disabled={!commentText.trim() || !isValid || isCreatingContent}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-coral-pink to-coral-orange rounded-lg hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200 flex items-center"
          >
            {isCreatingContent && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {(isTrack || isYouTubeVideo)
              ? (targetUser ? 'Share to Dancefloor' : 'Share to Commons')
              : selectedContentType === 'discussion'
                ? 'Start Discussion'
                : selectedContentType === 'review'
                  ? 'Share Review'
                  : 'Add to Listening Room'
            }
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renders artist confirmation UI for low-confidence YouTube extractions
   * Shows video info and extracted artist name, asking user to confirm before proceeding
   */
  const renderConfirmArtist = () => {
    if (!resolvedMusic) return null;

    const { response } = resolvedMusic;
    const data = response.data as YouTubeVideoDisplay;

    const handleConfirm = () => {
      // Proceed to music-resolved state - video entity already created
      setPanelState('music-resolved');
    };

    const handleGoBack = () => {
      setResolvedMusic(null);
      setMusicUrl('');
      setPanelState('url-input');
    };

    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-nunito font-semibold text-gray-900 mb-2">Confirm Artist</h2>
          <p className="text-sm font-light text-gray-600 leading-relaxed">
            We detected the following artist from this video. Please confirm this is correct.
          </p>
        </div>

        {/* Video Preview */}
        <div className="flex items-start mb-6">
          {data.thumbnailUrl && (
            <div className="relative mr-4 flex-shrink-0">
              <img
                src={data.thumbnailUrl}
                alt={data.title}
                className="w-32 h-20 rounded-lg object-cover shadow-lg"
              />
              {/* Play icon overlay */}
              <div className="absolute inset-0 bg-black/30 rounded-lg flex items-center justify-center">
                <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-coral-pink border-b-[6px] border-b-transparent ml-1" />
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 leading-tight mb-1 line-clamp-2">
              {data.title}
            </h3>
            <p className="text-sm text-gray-500">{data.channelName}</p>
          </div>
        </div>

        {/* Artist Confirmation */}
        <div className="bg-coral-pink/5 border border-coral-pink/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 mb-1">Detected Artist:</p>
          <p className="text-xl font-semibold text-coral-orange">{data.artistName}</p>
          {data.videoType === 'livePerformance' && data.eventName && (
            <p className="text-sm text-gray-500 mt-1">{data.eventName}</p>
          )}
          {data.videoType === 'musicVideo' && data.trackTitle && (
            <p className="text-sm text-gray-500 mt-1">Track: {data.trackTitle}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleGoBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200/50 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
          >
            Go Back
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-coral-pink to-coral-orange rounded-lg hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
          >
            Yes, that&apos;s correct
          </button>
        </div>
      </div>
    );
  };

  const renderError = () => (
    <div className="p-6">
      <div className="text-center py-6">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <X className="w-6 h-6 text-red-600" />
        </div>
        <p className="text-gray-600 mb-6">{getErrorMessage()}</p>
        <div className="flex justify-center space-x-3">
          <button
            onClick={() => setPanelState('url-input')}
            className="px-4 py-2 text-sm font-medium text-coral-deep-blue bg-white border border-coral-pink rounded-lg hover:bg-coral-pink hover:text-white focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
          >
            Try Again
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200/50 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  const renderPendingSuccess = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
      <div className="text-center py-6">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Submitted for Review</h3>
        <p className="text-gray-600 mb-6 max-w-md">
          This artist isn&apos;t yet in our system but we&apos;ll review this and your contribution will go live shortly.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-coral-pink to-coral-orange rounded-lg hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-coral-pink/20 transition-all duration-200"
        >
          Got it
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Enhanced Backdrop with blur */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-md z-40 transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Panel with glassmorphism - centered vertically */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl max-w-2xl w-full rounded-2xl overflow-hidden transform transition-all duration-300 ease-out">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-full transition-all duration-200 z-10"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>

          {panelState === 'url-input' && renderUrlInput()}
          {panelState === 'loading' && renderLoading()}
          {panelState === 'confirm-artist' && renderConfirmArtist()}
          {panelState === 'music-resolved' && renderMusicResolved()}
          {panelState === 'error' && renderError()}
          {panelState === 'pending-success' && renderPendingSuccess()}
        </div>
      </div>
    </>
  );
};

export default ShareMusicPanel;